import { markSvg } from "../brand/mark";

/**
 * The splash screen in the Maji brand (ADR 0023; design `SNz2d`, dark `j5MKma`): the mark and
 * the maji·tap wordmark on light sky, navy in dark mode, two white waves along the bottom.
 * Plain HTML with its own styles, no React and no CSS modules, so a Vite build can put it in
 * index.html (`@majitap/ui/splash`) and `LaunchScreen` can draw the very same thing: the screen
 * shows before the app's script has loaded and stays the same while it starts. It covers the
 * window; sheets opened over it (Choose company) come above it.
 */
export const SPLASH_CSS = `
.mt-splash {
  --splash-bg: #bde9fa;
  --splash-maji: #032554;
  --splash-tap: #0d6ba8;
  --splash-outline: #032554;
  --splash-company: #032554b3;
  --splash-spinner: #032554;
  --splash-wave-back: #ffffff80;
  --splash-wave-front: #ffffff;
  position: fixed;
  inset: 0;
  display: grid;
  grid-template-rows: 1fr auto;
  justify-items: center;
  padding-bottom: max(2.75rem, env(safe-area-inset-bottom));
  overflow: hidden;
  background: var(--splash-bg);
}
:root[data-theme="dark"] .mt-splash {
  --splash-bg: #032554;
  --splash-maji: #ffffff;
  --splash-tap: #7ed3f0;
  --splash-outline: #bde9fa;
  --splash-company: #bde9fab3;
  --splash-spinner: #ffffff;
  --splash-wave-back: #0a3870;
  --splash-wave-front: #0d4a8c;
}
.mt-splash-center {
  position: relative;
  z-index: 1;
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 1.125rem;
  padding-bottom: 2.5rem;
}
.mt-splash-mark {
  width: auto;
  height: 9.25rem;
}
.mt-splash-name {
  position: relative;
  margin: 0;
  color: var(--splash-maji);
  font-family: "Fredoka Variable", "Fredoka", system-ui, sans-serif;
  font-size: 2.875rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1;
}
.mt-splash-tap {
  color: var(--splash-tap);
}
/* the app is on its way: a ring turning under the name, CSS only, so it turns before the script.
   It hangs below the name out of the layout, so the mark stays where iOS's startup images draw it. */
.mt-splash-spinner {
  position: absolute;
  top: calc(100% + 1.75rem);
  left: 50%;
  width: 1.75rem;
  height: 1.75rem;
  margin-left: -0.875rem;
  box-sizing: border-box;
  border: 3px solid color-mix(in srgb, var(--splash-spinner) 25%, transparent);
  border-top-color: var(--splash-spinner);
  border-radius: 50%;
  animation: mt-splash-spin 0.9s linear infinite;
}
@keyframes mt-splash-spin {
  to {
    transform: rotate(360deg);
  }
}
/* slower, not still: it still says the app is loading */
@media (prefers-reduced-motion: reduce) {
  .mt-splash-spinner {
    animation-duration: 2.4s;
  }
}
.mt-splash-company {
  position: relative;
  z-index: 1;
  margin: 0;
  color: var(--splash-company);
  font-family: "Inter Variable", system-ui, sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
}
.mt-splash-waves {
  position: absolute;
  inset: auto 0 0;
  width: 100%;
  height: 13.75rem;
}
`;

/** The mark, the wordmark and the spinner, as the splash shows them. */
export const SPLASH_CENTER = `${markSvg({
  outline: "var(--splash-outline)",
  attrs: 'class="mt-splash-mark" aria-hidden="true"',
})}<p class="mt-splash-name">maji<span class="mt-splash-tap">tap</span><span class="mt-splash-spinner" aria-hidden="true"></span></p>`;

/** The two waves along the bottom (the design's Wave Back and Wave Front). */
export const SPLASH_WAVES = `<svg class="mt-splash-waves" viewBox="0 0 390 220" preserveAspectRatio="none" aria-hidden="true"><path style="fill:var(--splash-wave-back)" d="M0 70C70 30 150 28 220 62S340 104 390 52V220H0Z"></path><path style="fill:var(--splash-wave-front)" d="M0 118C80 86 170 140 255 116S360 82 390 100V220H0Z"></path></svg>`;

/** The splash as HTML, for index.html's root before the script runs. */
export const splashHtml = () =>
  `<div class="mt-splash" role="status" aria-label="Opening MajiTap"><style>${SPLASH_CSS}</style><div class="mt-splash-center">${SPLASH_CENTER}</div><div style="display:contents">${SPLASH_WAVES}</div></div>`;
